import { retry } from '@repo/ts-query-utils/retry'
import { retryDelay } from '@repo/ts-query-utils/retry-delay'
import * as Sentry from '@sentry/browser'
import type { Breadcrumb } from '@sentry/browser'
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import type { Query } from '@tanstack/react-query'
import { isError, isNetworkError } from 'utils/js'
import { reportAppRendererNetworkError } from './desktop/app-renderer-online'

/**
 * A query hash spells out the query's variables. A query whose variables are user data, such as a
 * pasted link or a file name, sets `meta: { sensitive: true }` to be reported without it.
 * The RPC interceptor hides payloads by default, but only an explicit `true` reaches this breadcrumb.
 */
export function getQueryErrorBreadcrumb(
  error: unknown,
  query: Pick<Query<unknown, unknown>, 'queryHash' | 'meta' | 'state' | 'getObserversCount'>,
): Breadcrumb {
  const queryHash = query.meta?.sensitive === true ? undefined : query.queryHash

  return {
    category: 'query.error',
    level: 'warning',
    message: queryHash ?? '[sensitive query key]',
    data: {
      ...(queryHash === undefined ? {} : { queryHash }),
      fetchFailureCount: query.state.fetchFailureCount,
      observers: query.getObserversCount(),
      message: error instanceof Error ? error.message : String(error),
    },
  }
}

export const queryClient = new QueryClient({
  queryCache: new QueryCache({
    onError(error, query) {
      if (isNetworkError(error)) {
        reportAppRendererNetworkError()
      }
      Sentry.addBreadcrumb(getQueryErrorBreadcrumb(error, query))
    },
  }),
  mutationCache: new MutationCache({
    onError(error) {
      if (isNetworkError(error)) {
        reportAppRendererNetworkError()
      }
    },
  }),
  defaultOptions: {
    queries: {
      retry,
      retryDelay,
    },
    mutations: {
      onError(error, _variables, context) {
        if (isNetworkError(error)) {
          return
        }

        if (isError(error) && !String(error.message)?.startsWith('CF_')) {
          // eslint-disable-next-line no-console
          console.error(error, context)
        }
      },
    },
  },
})
